/**
 * The pure playlist cursor for the login background music (ADR-0146): the tiny,
 * Web-Audio-free sequencing brain the {@link ./musicPlayer.MusicPlayer} drives its
 * synth from (via the {@link ./musicDirector.MusicDirector}, which wraps this cursor to
 * layer the duel-combat override over it). Kept deliberately separate from the audio
 * graph - exactly the split the
 * {@link ./soundObserver} keeps from {@link ./audioManager} - so the ordering and the
 * loop-back-after-the-last-track are unit-tested in plain node, and the imperative
 * shell around SpessaSynth stays untested (like `audioManager`).
 *
 * The cursor holds an ordered list of track ids and points at one of them. `advance()`
 * steps to the next id and **wraps to the first after the last**, so the login playlist
 * (`oldult06 -> oldult05 -> oldult03 -> britain1 -> britain2 -> oldult06 -> ...`, see
 * {@link ../sim/content.LOGIN_PLAYLIST}) repeats forever: the director calls `advance()`
 * each time a login track finishes and plays whatever `current()` then returns. A single-track
 * list simply repeats that track; an empty list is a no-op the player treats as "nothing
 * to play".
 */
export class Playlist {
  private readonly tracks: readonly string[];
  private index = 0;

  /**
   * @param tracks The ordered track ids to cycle. Copied defensively so a later
   *   mutation of the caller's array can't desync the cursor.
   */
  constructor(tracks: readonly string[]) {
    this.tracks = [...tracks];
  }

  /** Whether there is anything to play at all (an empty playlist is inert). */
  get isEmpty(): boolean {
    return this.tracks.length === 0;
  }

  /** How many tracks the playlist cycles. */
  get length(): number {
    return this.tracks.length;
  }

  /** The track ids, in order (read-only) - for a cursor-free availability scan. */
  get ids(): readonly string[] {
    return this.tracks;
  }

  /** The zero-based position of the track {@link current} points at. */
  get position(): number {
    return this.index;
  }

  /** The id of the track currently pointed at, or `undefined` when the list is empty. */
  current(): string | undefined {
    return this.tracks[this.index];
  }

  /**
   * Step to the next track, wrapping from the last back to the first so the playlist
   * loops indefinitely, and return the new {@link current} id. A no-op returning
   * `undefined` when the list is empty.
   */
  advance(): string | undefined {
    if (this.isEmpty) return undefined;
    this.index = (this.index + 1) % this.tracks.length;
    return this.current();
  }

  /** Jump the cursor back to the first track (used when (re)starting playback). */
  reset(): void {
    this.index = 0;
  }

  /**
   * Jump the cursor to `index`, wrapping into range (so any integer is valid) - used to
   * open a playlist on a chosen/random track (the Arena playlist's per-run random start,
   * ADR-0210). A no-op on an empty list.
   */
  seek(index: number): void {
    if (this.isEmpty) return;
    const n = this.tracks.length;
    this.index = ((index % n) + n) % n;
  }
}
